import { apiAuthCaller } from '@services/apiCaller';
import { action, computed, observable } from 'mobx';
import { IRespData } from '@components/destinations/liveEvents/interface';
import SourceFactory from '@components/sources/sourceFactory';
import { getApiErrorMessage } from '@components/common/util/util';
import { IRootStore } from './index';
import { ISourceStore } from './source';
import { ITransformationStore } from './transformation';
import { IDestinationDef } from './destinationDefsList';
import { AxiosResponse } from 'axios';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const jsonDiff = require('json-diff');

interface IErrors {
  items?: {
    statusCode: number;
    count: number;
    lastOccurred: string;
  }[];
  selected?: {
    timestamp: number;
    sampleEvent: Record<string, unknown>;
    sampleResponse: Record<string, unknown>;
    eventName: string;
    eventType: string;
  }[];
}

interface EventStats {
  graph: {
    bucket: number;
    totalFailed: number | null;
    totalDelivered: number | null;
  }[];
  totalFailed: number;
  totalDelivered: number;
}

interface EventStatParams {
  sourceId?: string;
  start: string;
  aggregationMinutes: number;
}
interface SampleErrorsParams {
  sourceId?: string;
  start: string;
  statusCode: number;
}

export enum AuthStatus {
  // TODO: This can be removed as we are looking to put authStatus into destination config
  UNSUPPORTED = 'unsupported',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export type DestinationConfig = {
  useNativeSDK?: Record<string, boolean>;
  rudderAccountId?: string;
  authStatus?: AuthStatus;
  webhookUrl?: string;
  [key: string]: string | undefined | Record<string, unknown>;
};

type DestinationAccount = {
  id: string;
  name: string;
  createdAt: string;
  metadata: { email: string };
};

export type SecretConfig = Record<string, unknown>;
export interface IDestination {
  id: string;
  name: string;
  enabled: boolean;
  config: DestinationConfig;
  secretConfig: SecretConfig;
  destinationDefinition: IDestinationDef;
  sources: ISourceStore[];
  transformations: ITransformationStore[];
  eventStats: EventStats | undefined;
  errors: IErrors | undefined;
}
export interface IDestinationStore extends IDestination {
  setName(name: string): void;
  toggleEnabled(config?: DestinationConfig): void;
  updateConfig(
    config: DestinationConfig,
    hardUpdate?: boolean,
    onSuccess?: () => void,
    onError?: () => void,
  ): Promise<boolean>;
  updateName(name: string): void;
  getLiveEvents(latestEventId: number, from: number): Promise<IRespData[]>;
  isMapped(): boolean;
  isWarehouse(): boolean;
  setConfig(data: { config: DestinationConfig; secretConfig: SecretConfig }): void;
  getEventsStats(params: EventStatParams, callback?: () => void): void;
  getSampleErrors(params: SampleErrorsParams): void;
  getAccounts(): Promise<DestinationAccount[]>;
  deleteAccount(accountId: string, onSuccess: () => void): void;
  changeActiveAccount(accountId: string, onSuccess: () => void): void;
}

interface IUpdateDestinationBody {
  enabled: boolean;
  config?: DestinationConfig;
}

export class DestinationStore implements IDestinationStore {
  @observable public id: string;

  @observable public name: string;

  @observable public enabled: boolean;

  @observable public config: DestinationConfig;

  @observable public secretConfig: SecretConfig;

  @observable public destinationDefinition: IDestinationDef;

  @observable public rootStore: IRootStore;

  @observable eventStats: EventStats | undefined;

  @observable errors: IErrors | undefined;

  constructor(destination: IDestination, rootStore: IRootStore) {
    this.id = destination.id;
    this.name = destination.name;
    this.enabled = destination.enabled;
    this.config = destination.config;
    this.secretConfig = destination.secretConfig;
    this.destinationDefinition = destination.destinationDefinition;
    this.rootStore = rootStore;
    const prevState = rootStore.destinationsListStore.destinationById(this.id);
    this.errors = prevState?.errors;
    this.eventStats = prevState?.eventStats;
  }

  @action.bound
  public setName(name: string): void {
    this.name = name;
  }

  @computed get sources() {
    const sourceIds: string[] = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const key in this.rootStore.connectionsStore.connections) {
      if (this.rootStore.connectionsStore.connections[key].includes(this.id)) {
        sourceIds.push(key);
      }
    }
    return this.rootStore.sourcesListStore.sources.filter((source) =>
      sourceIds.includes(source.id),
    );
  }

  @computed get transformations() {
    return this.rootStore.transformationsListStore.transformations.filter(
      (transformation: ITransformationStore) =>
        transformation.destinationIds && transformation.destinationIds.includes(this.id),
    );
  }

  @action.bound
  /**
   * @param {object} config - The configuration for the destination
   * This is a more generic object and varies from destination to destination
   * For OAuth supported destinations, there will be a field `rudderAccountId` which indicates
   * that the destination has been authorised by the account with this id.
   *
   * This object contains a property `authStatus` whose explanation is provided below
   * If the destination has been disable due to an exception such revoking of an account,
   * then when the user tries to enable the destination he/she will be asked to re-authorise.
   * After re-auth is successful we would need to change the authStatus from INACTIVE to
   * ACTIVE explicitly. This parameter indicates the authorisation status for a destination
   */
  public async toggleEnabled(config?: DestinationConfig) {
    try {
      const {
        userStore: { token },
        messagesStore,
      } = this.rootStore;
      if (
        this.enabled === false &&
        (!this.config || Object.keys(this.config).length === 0) &&
        (!this.secretConfig || Object.keys(this.secretConfig).length === 0)
      ) {
        messagesStore.showErrorMessage('Please update your destination settings to enable');
        return;
      }
      let updateBody: IUpdateDestinationBody = { enabled: !this.enabled };
      if (config) {
        updateBody = {
          ...updateBody,
          config,
        };
      }
      await apiAuthCaller(token).post(`/destinations/${this.id}`, updateBody);
      this.enabled = !this.enabled;
      if (config) {
        this.config = config;
      }
    } catch (error) {}
  }

  @action.bound
  public async updateConfig(
    config: DestinationConfig,
    hardUpdate = false,
    onSuccess: () => void = () => null,
    onError: () => void = () => null,
  ) {
    const {
      userStore: { token },
      messagesStore,
    } = this.rootStore;
    try {
      const updateUrl = `/destinations/${this.id}${hardUpdate ? '?hardUpdate=true' : ''}`;
      const res = await apiAuthCaller(token).post(updateUrl, {
        config,
      });
      const configDiff = jsonDiff.diff(this.config, res.data.config);
      const secretConfigDiff = jsonDiff.diff(this.secretConfig, res.data.secretConfig);
      this.config = res.data.config;
      this.secretConfig = res.data.secretConfig;
      onSuccess();
      if (configDiff || secretConfigDiff) {
        if (configDiff && Object.prototype.hasOwnProperty.call(configDiff, 'syncFrequency')) {
          window.rudderanalytics.track('sync frequency updated', {
            updatedConfig: configDiff.syncFrequency,
            destID: this.id,
            destName: this.name,
            destType: this.destinationDefinition.displayName,
          });
        }
        window.rudderanalytics.track('dest config updated', {
          newConfig: configDiff,
          newSecretConfig: secretConfigDiff,
          destID: this.id,
          destName: this.name,
          destType: this.destinationDefinition.displayName,
        });
      }
      return true;
    } catch (error) {
      onError();
      messagesStore.showErrorMessage(
        getApiErrorMessage(error, 'Failed to save destination configuration'),
      );
      window.rudderanalytics.track('dest config update failure', {
        error,
        destID: this.id,
        destName: this.name,
        destType: this.destinationDefinition.displayName,
      });
      return false;
    }
  }

  @action.bound
  /**
   * updateName
   */
  public async updateName(name: string) {
    try {
      const {
        destinationsListStore,
        userStore: { token },
        messagesStore,
      } = this.rootStore;
      if (!destinationsListStore.isDestNameUnique(name, this.name)) {
        messagesStore.showErrorMessage('Please choose a unique destination name');
        return;
      }
      const updateUrl = `/destinations/${this.id}/updateName`;
      const res = await apiAuthCaller(token).post(updateUrl, {
        name,
      });
      this.name = res.data.name;
      window.rudderanalytics.track('dest name updated', {
        updatedDestName: this.name,
        destID: this.id,
        destType: this.destinationDefinition.displayName,
      });
    } catch (error) {
      window.rudderanalytics.track('dest name update failure', {
        destID: this.id,
        destType: this.destinationDefinition.displayName,
        error,
      });
    }
  }

  @action.bound
  public async disconnectTransformations() {
    try {
      const { token } = this.rootStore.userStore;
      await apiAuthCaller(token).post(`/destinations/${this.id}/disconnectAllTransformations`);
    } catch (error) {}
  }

  public async getLiveEvents(latestEventId: number, timestamp: number) {
    const {
      userStore: { token },
    } = this.rootStore;
    try {
      const resp = await apiAuthCaller(token).get(
        `/eventDeliveryStatus?id=${latestEventId}&from=${timestamp}`,
        { headers: { 'X-DESTINATION-ID': this.id } },
      );
      return resp.data;
    } catch (err) {
      return [];
    }
  }

  public isMapped() {
    return this.sources.some((source) =>
      source.config?.constants?.some(
        (obj: { key: string; value: string }) =>
          obj.key === 'context.mappedToDestination' && obj.value,
      ),
    );
  }

  public isWarehouse() {
    return this.sources.some((s) => SourceFactory(s.category).isWarehouse());
  }

  public setConfig(data: { config: DestinationConfig; secretConfig: SecretConfig }) {
    this.config = data.config;
    this.secretConfig = data.secretConfig;
  }

  @action.bound
  async getAccounts() {
    const { token } = this.rootStore.userStore;
    const response: AxiosResponse<DestinationAccount[]> = await apiAuthCaller(token).get(
      `/destination/accounts/${
        this.destinationDefinition.config?.auth?.role ||
        this.destinationDefinition.name.toLowerCase()
      }`,
    );
    return response.data;
  }

  @action.bound
  async deleteAccount(accountId: string, onSuccess: () => void) {
    const {
      userStore: { token },
      messagesStore,
    } = this.rootStore;
    try {
      await apiAuthCaller(token).delete(`/destination/accounts/${accountId}`);
      onSuccess();
    } catch (err) {
      messagesStore.showErrorMessage(getApiErrorMessage(err, 'Failed to delete account'));
    }
  }

  @action.bound
  async changeActiveAccount(accountId: string, onSuccess: () => void) {
    const config = {
      ...this.config,
      rudderAccountId: accountId,
    };
    await this.updateConfig(config, false, onSuccess);
  }

  public getEventsStats(params: EventStatParams, callback: () => void) {
    const { sourceId, start, aggregationMinutes } = params;
    const { token } = this.rootStore.userStore;
    apiAuthCaller(token)
      .get(`/destinations/${this.id}/eventMetrics`, {
        params: {
          sourceId,
          start,
          aggregationMinutes,
        },
      })
      .then((res) => {
        const { errors, aggregates, totalDelivered, totalFailed } = res.data;
        this.errors = {
          items: errors,
        };
        this.eventStats = {
          graph: aggregates,
          totalDelivered,
          totalFailed,
        };
      })
      .catch((err) => {
        this.rootStore.messagesStore.showErrorMessage(
          getApiErrorMessage(err, 'Failed to get destination events data'),
        );
      })
      .finally(() => {
        callback?.();
      });
  }

  public getSampleErrors(params: SampleErrorsParams) {
    this.errors = {
      ...this.errors,
      selected: undefined,
    };
    const { sourceId, start, statusCode } = params;
    const { token } = this.rootStore.userStore;
    apiAuthCaller(token)
      .get(`/destinations/${this.id}/errors`, {
        params: {
          sourceId,
          start,
          statusCode,
        },
      })
      .then((res) => {
        this.errors = {
          ...this.errors,
          selected: res.data.result,
        };
      })
      .catch((err) => {
        this.rootStore.messagesStore.showErrorMessage(
          getApiErrorMessage(err, 'Failed to get sample errors'),
        );
      });
  }
}
